import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH} from 'procedures/design/ProcedureDesignConstants';
import * as ProcedureRunDAO from 'procedures/network/ProcedureRunDAO';
import * as ProcedureRunPlanDAO from 'procedures/network/procedureRunPlanDAO';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import {SettingsLabel} from 'ui-library/SettingsField';

// Case 1: Being created - no IDs needed, setDescription required
interface CreatingProps {
  isBeingCreated: true;
  description: string;
  setDescription: (description: string) => void;
  procedureRunId?: never;
  procedureRunPlanId?: never;
  studyId?: never;
  refetch?: never;
}

// Case 2: Editing a procedure run - only procedureRunId needed
interface EditingProcedureRunProps {
  isBeingCreated: false;
  description: string;
  procedureRunId: string;
  refetch: () => void;
  procedureRunPlanId?: never;
  studyId?: never;
  setDescription?: never;
}

// Case 3: Editing a procedure run plan - procedureRunPlanId and studyId needed
interface EditingProcedureRunPlanProps {
  isBeingCreated: false;
  description: string;
  procedureRunPlanId: string;
  studyId: string;
  refetch: () => void;
  procedureRunId?: never;
  setDescription?: never;
}

export type ProcedureRunDescriptionEditorProps =
  | CreatingProps
  | EditingProcedureRunProps
  | EditingProcedureRunPlanProps;

type EditingProps = EditingProcedureRunProps | EditingProcedureRunPlanProps;

async function updateDescription(props: EditingProps, description: string): Promise<void> {
  if (props.procedureRunId) {
    await ProcedureRunDAO.updateProcedureRun(
      {
        id: props.procedureRunId,
        description,
      },
      {supportsAsync: false}
    );
  } else if (props.procedureRunPlanId) {
    await ProcedureRunPlanDAO.updateProcedureRunPlan({
      id: props.procedureRunPlanId,
      studyId: props.studyId,
      description,
    });
  }
  props.refetch();
}

export default function ProcedureRunDescriptionEditor(
  props: ProcedureRunDescriptionEditorProps
): JSX.Element | null {
  const {isBeingCreated, description} = props;

  return (
    <FormGroup>
      <div
        className={css(
          isBeingCreated ? styles.descriptionFormInputStyle : styles.descriptionEdibleTextFieldTitle
        )}
      >
        <SettingsLabel isRequired={false} label="Description" name="description" withBottomMargin={false} />
      </div>
      {isBeingCreated ? (
        <FormInput
          disabled={false}
          onChange={(e) => {
            props.setDescription(e.target.value);
          }}
          id="description"
          type={TextFieldType.TEXTAREA}
          value={description}
          maxLength={MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH}
          autoSize={true}
        />
      ) : (
        <EditableTextField
          isDisabled={false}
          requestChange={(updatedDescription) => {
            updateDescription(props, updatedDescription as string);
          }}
          emptyValueAllowed={true}
          value={description}
          maxLength={MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH}
          type={TextFieldType.TEXTAREA}
          viewerClassName={css(styles.textValueBlock)}
          autoSize={true}
        />
      )}
    </FormGroup>
  );
}

const styles = StyleSheet.create({
  descriptionEdibleTextFieldTitle: {
    marginLeft: -10,
  },
  descriptionFormInputStyle: {
    marginBottom: 5,
  },
  textValueBlock: {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
  },
});
